/**
 * Pack 선택 모달 — 서버 `/admin/ontology/packs` 응답 카탈로그를 그룹 탭 + 다중 체크 UX 로 제시.
 *
 * 구조:
 * - 상단 Starter 라디오: preset 6개(Pioneer Default / Engineering / Product / Sales / Marketing / Design)
 *   + "Custom" 옵션. 라디오 선택 시 해당 preset.packIds 로 체크 상태를 자동 채운다.
 * - 그룹 탭: foundation / build / strategy / gtm / craft / cross-cutting (6개). 현재 탭의 팩만 카드로 표시.
 * - 팩 카드: 체크박스 + 이름 + 설명 + kinds/relations/shapes 카운트 +
 *   **"N blocks" 미니라벨** (호버 시 blockIds 툴팁) + **확장 영역의 블럭 칩 리스트**
 * - 우측 사이드 미리보기: 선택된 팩의 머지 카운트 + **선택 팩 전체의 고유 블럭 수 + 카테고리 분포**
 * - 하단: 취소 · Apply packs (선택 0개면 disabled).
 *
 * Starter 라디오 동기화: 체크박스 셋과 preset.packIds 셋이 **정확히 일치** 하면 해당 preset 선택.
 * 일치 없으면 "Custom" 으로 표시 — 사용자가 Starter 를 건드리지 않은 상태 유지.
 */
import { useEffect, useMemo, useState } from "react"
import type {
	AdminClient,
	BlockCategory,
	OntologyBlock,
	OntologyPack,
	OntologyPreset,
	PackGroup
} from "../../api/admin-client.ts"
import { mergePackCounts } from "./merge-packs.ts"
import { uniqueBlockIds } from "./pack-block-utils.ts"

interface PackPickerProps {
	readonly packs: ReadonlyArray<OntologyPack>
	/** Starter 라디오 소스 — preset 카탈로그. */
	readonly presets: ReadonlyArray<OntologyPreset>
	/**
	 * 블럭 카탈로그 드릴다운에 사용. OntologyEditor 가 best-effort 로 로드한 뒤 넘기거나,
	 * 본 컴포넌트가 직접 client 로 fetch (prop 우선). 없으면 툴팁/확장 영역은 id 만 표시.
	 */
	readonly blocks?: ReadonlyArray<OntologyBlock>
	/** blocks prop 이 없을 때 본 컴포넌트가 fallback 으로 직접 fetch 에 쓰는 클라이언트. */
	readonly client?: AdminClient
	readonly loading?: boolean
	readonly currentPackIds?: ReadonlyArray<string>
	readonly hasUnsavedChanges: boolean
	readonly onClose: () => void
	readonly onApply: (selectedPackIds: ReadonlyArray<string>) => void
}

/** 탭 순서 — 서버 PACK_GROUPS 와 동일. 라벨은 대시보드 UI 카피. */
const GROUP_TABS: ReadonlyArray<{ readonly group: PackGroup; readonly label: string }> = [
	{ group: "foundation", label: "기반" },
	{ group: "build", label: "개발" },
	{ group: "strategy", label: "전략" },
	{ group: "gtm", label: "GTM" },
	{ group: "craft", label: "기획" },
	{ group: "cross-cutting", label: "범분야" }
]

/** PII 경고 배지를 띄울 팩 id 셋 — 현재 sales 팩만. */
const PII_WARNING_PACK_IDS = new Set<string>(["sales"])

export function PackPicker(props: PackPickerProps) {
	const [selected, setSelected] = useState<ReadonlySet<string>>(() => new Set(props.currentPackIds ?? []))
	const [activeGroup, setActiveGroup] = useState<PackGroup>("foundation")

	// prop 으로 주입된 blocks 우선. 없으면 client 로 fetch 시도 (best-effort).
	const [fetchedBlocks, setFetchedBlocks] = useState<ReadonlyArray<OntologyBlock>>([])
	const { blocks: blocksProp, client: clientProp } = props
	useEffect(() => {
		if (blocksProp || !clientProp) return
		let cancelled = false
		void (async () => {
			try {
				const list = await clientProp.getOntologyBlocks()
				if (!cancelled) setFetchedBlocks(list)
			} catch {
				// 무시 — 카드 확장 영역은 id 만 표시
			}
		})()
		return () => {
			cancelled = true
		}
	}, [blocksProp, clientProp])
	const blocks = blocksProp ?? fetchedBlocks

	const packById = useMemo(() => new Map(props.packs.map((p) => [p.id, p])), [props.packs])
	const blockById = useMemo(() => new Map(blocks.map((b) => [b.id, b])), [blocks])

	const selectedPacks = useMemo(
		(): ReadonlyArray<OntologyPack> =>
			[...selected].flatMap((id) => {
				const p = packById.get(id)
				return p ? [p] : []
			}),
		[selected, packById]
	)
	const counts = useMemo(() => mergePackCounts(selectedPacks), [selectedPacks])

	// 선택된 팩 전체의 블럭 합집합 + 카테고리 분포 — 사이드 미리보기.
	const blockUnion = useMemo(() => uniqueBlockIds(selectedPacks), [selectedPacks])
	const categoryDist = useMemo((): ReadonlyArray<{
		readonly category: BlockCategory
		readonly count: number
	}> => {
		const counts: Record<BlockCategory, number> = {
			actor: 0,
			content: 0,
			event: 0,
			product: 0,
			place: 0,
			structure: 0,
			decision: 0
		}
		for (const id of blockUnion) {
			const block = blockById.get(id)
			if (block) counts[block.category]++
		}
		// 0 이 아닌 카테고리만 카탈로그 표준 순서대로 반환
		const order: ReadonlyArray<BlockCategory> = [
			"actor",
			"content",
			"event",
			"product",
			"place",
			"structure",
			"decision"
		]
		return order.filter((c) => counts[c] > 0).map((c) => ({ category: c, count: counts[c] }))
	}, [blockUnion, blockById])

	/** 현재 체크박스 셋과 packIds 셋이 정확히 일치하는 preset id — 없으면 undefined (Custom). */
	const matchedPresetId = useMemo((): string | undefined => {
		for (const preset of props.presets) {
			if (preset.packIds.length !== selected.size) continue
			let allMatch = true
			for (const id of preset.packIds) {
				if (!selected.has(id)) {
					allMatch = false
					break
				}
			}
			if (allMatch) return preset.id
		}
		return undefined
	}, [props.presets, selected])

	function togglePack(id: string) {
		setSelected((prev) => {
			const next = new Set(prev)
			if (next.has(id)) next.delete(id)
			else next.add(id)
			return next
		})
	}

	function applyStarter(preset: OntologyPreset) {
		setSelected(new Set(preset.packIds))
		// 선택된 preset 의 첫 팩이 속한 그룹으로 탭 이동하면 즉시 카드가 보인다
		const firstPackId = preset.packIds[0]
		const firstPack = firstPackId !== undefined ? packById.get(firstPackId) : undefined
		if (firstPack) setActiveGroup(firstPack.group)
	}

	const visiblePacks = props.packs.filter((p) => p.group === activeGroup)
	const canApply = selected.size > 0

	return (
		<dialog
			className="modal-backdrop"
			open
			onMouseDown={(e) => e.currentTarget === e.target && props.onClose()}
		>
			<div className="modal pack-picker-modal" onMouseDown={(e) => e.stopPropagation()}>
				<div className="modal-head">
					<h2>Pack 선택</h2>
					<p>필요한 팩을 조합해 슬라이스를 구성합니다. Starter 로 시작하거나 직접 체크하세요.</p>
				</div>

				<div className="modal-body">
					{props.hasUnsavedChanges && (
						<div className="alert alert-warn" style={{ marginBottom: 14 }}>
							현재 저장되지 않은 변경사항이 있습니다. 팩을 적용하면 덮어써집니다.
						</div>
					)}

					{props.loading ? (
						<p className="muted">팩 카탈로그를 불러오는 중…</p>
					) : props.packs.length === 0 ? (
						<p className="muted">사용 가능한 팩이 없습니다.</p>
					) : (
						<div className="pack-picker-layout">
							<div className="pack-picker-main">
								<fieldset className="pack-starter">
									<legend className="pack-starter-legend">스타터</legend>
									<div className="pack-starter-list">
										{props.presets.map((preset) => {
											const isMatched = matchedPresetId === preset.id
											return (
												<label
													key={preset.id}
													className={`pack-starter-option${isMatched ? " is-selected" : ""}`}
												>
													<input
														type="radio"
														name="pack-starter"
														checked={isMatched}
														onChange={() => applyStarter(preset)}
													/>
													<span className="pack-starter-icon" aria-hidden>
														{preset.icon || "◎"}
													</span>
													<span className="pack-starter-label">{preset.displayName}</span>
												</label>
											)
										})}
										<label
											className={`pack-starter-option${matchedPresetId === undefined ? " is-selected" : ""}`}
										>
											<input
												type="radio"
												name="pack-starter"
												checked={matchedPresetId === undefined}
												readOnly
											/>
											<span className="pack-starter-icon" aria-hidden>
												✨
											</span>
											<span className="pack-starter-label">직접 선택</span>
										</label>
									</div>
								</fieldset>

								<div className="pack-group-tabs" role="tablist">
									{GROUP_TABS.map((tab) => {
										const isActive = activeGroup === tab.group
										const groupCount = props.packs.filter((p) => p.group === tab.group).length
										return (
											<button
												type="button"
												key={tab.group}
												role="tab"
												aria-selected={isActive}
												className={`pack-group-tab${isActive ? " is-active" : ""}`}
												onClick={() => setActiveGroup(tab.group)}
											>
												{tab.label}
												<span className="pack-group-tab-count">{groupCount}</span>
											</button>
										)
									})}
								</div>

								<div className="pack-grid">
									{visiblePacks.length === 0 ? (
										<p className="muted">이 그룹에는 팩이 없습니다.</p>
									) : (
										visiblePacks.map((pack) => {
											const isChecked = selected.has(pack.id)
											const hasPii = PII_WARNING_PACK_IDS.has(pack.id)
											const blockCount = pack.blockIds.length
											// 툴팁에는 전체 blockIds 를 노출해 hover 만으로 조합 힌트 파악
											const blocksTooltip =
												blockCount > 0 ? `Blocks: ${pack.blockIds.join(", ")}` : "블럭 조합 없음"
											return (
												<label key={pack.id} className={`pack-card${isChecked ? " is-selected" : ""}`}>
													<div className="pack-card-head">
														<input type="checkbox" checked={isChecked} onChange={() => togglePack(pack.id)} />
														<span className="pack-card-icon" aria-hidden>
															{pack.icon || "◎"}
														</span>
														<strong>{pack.displayName}</strong>
														{blockCount > 0 && (
															<span
																className="pack-card-block-count"
																title={blocksTooltip}
																aria-label={`${blockCount}개 블럭`}
															>
																{blockCount}개 블럭
															</span>
														)}
													</div>
													<p className="pack-card-desc">{pack.description}</p>
													<div className="pack-card-stats">
														<span>kinds {pack.slice.kinds.length}</span>
														<span>relations {pack.slice.relations.length}</span>
														<span>shapes {pack.slice.shapes.length}</span>
													</div>
													{pack.blockIds.length > 0 && (
														<div
															className="pack-card-blocks"
															aria-label={`${pack.displayName} 이 사용하는 블럭`}
														>
															{pack.blockIds.map((bid) => {
																const block = blockById.get(bid)
																return (
																	<span
																		key={bid}
																		className="chip pack-card-block-chip"
																		title={block?.description ?? bid}
																	>
																		{block ? (
																			<>
																				<span aria-hidden style={{ marginRight: 4 }}>
																					{block.icon}
																				</span>
																				{block.displayName}
																			</>
																		) : (
																			bid
																		)}
																	</span>
																)
															})}
														</div>
													)}
													{hasPii && (
														<span className="badge badge-warning pack-card-pii">고객 PII 포함 가능</span>
													)}
												</label>
											)
										})
									)}
								</div>
							</div>

							<aside className="pack-preview-side" aria-label="머지 미리보기">
								<div className="pack-preview-head">
									<span className="pack-preview-kicker">병합 미리보기</span>
									<strong>{selected.size}개 팩 선택</strong>
								</div>
								<ul className="pack-preview-stats">
									<li>
										<span className="pack-preview-stat-label">종류</span>
										<span className="pack-preview-stat-value">{counts.kinds}</span>
									</li>
									<li>
										<span className="pack-preview-stat-label">도메인</span>
										<span className="pack-preview-stat-value">{counts.domains}</span>
									</li>
									<li>
										<span className="pack-preview-stat-label">관계</span>
										<span className="pack-preview-stat-value">{counts.relations}</span>
									</li>
									<li>
										<span className="pack-preview-stat-label">형태</span>
										<span className="pack-preview-stat-value">{counts.shapes}</span>
									</li>
								</ul>

								{blockUnion.length > 0 && (
									<div className="pack-preview-blocks">
										<span className="pack-preview-kicker">블럭</span>
										<div className="pack-preview-blocks-head">
											<strong>{blockUnion.length}</strong>
											<span className="muted" style={{ fontSize: 11 }}>
												고유 블럭
											</span>
										</div>
										{categoryDist.length > 0 && (
											<ul className="pack-preview-cat-dist" aria-label="카테고리 분포">
												{categoryDist.map((c) => (
													<li key={c.category} className="pack-preview-cat-row">
														<span className="pack-preview-cat-label">{c.category}</span>
														<span className="pack-preview-cat-value">{c.count}</span>
													</li>
												))}
											</ul>
										)}
									</div>
								)}

								{selected.size > 0 && (
									<div className="pack-preview-chips">
										{[...selected].map((id) => {
											const pack = packById.get(id)
											if (!pack) return null
											return (
												<span key={id} className="chip pack-preview-chip">
													{pack.icon} {pack.displayName}
												</span>
											)
										})}
									</div>
								)}
							</aside>
						</div>
					)}
				</div>

				<div className="modal-foot">
					<button type="button" className="btn btn-ghost" onClick={props.onClose}>
						취소
					</button>
					<button
						type="button"
						className="btn btn-primary"
						disabled={!canApply}
						onClick={() => props.onApply([...selected])}
					>
						팩 적용
					</button>
				</div>
			</div>
		</dialog>
	)
}
